import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  Button,
  Row,
  Col,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";
import ReactTable from "react-table";
import TablePagination from "components/Table/TablePagination";
import strings from "../../core/translate";
import GarageSectionHeader from "../../components/Garage/GarageSectionHeader";
import RowActions, {
  editAction,
  deleteAction,
} from "components/Table/RowActions";
import SelectRow from "../../components/Select/SelectRow";
import { Scopes } from "../../shared/scopes.enum";

class MerchantUserList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = {
      isLoading: true,
      dataSource: [],
      merchants: [],
      users: [],
      selectedMerchant: "",
      selectedBranch: "",
      branchName: "",
      canWrite: false
    };
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    const storedMerchantId = sessionStorage.getItem("merchantId");
    const routeState = this.props.location.state || {};
    const { selectedMerchant: stateMerchant } = routeState;
    const { selectedBranch } = this.props.match.params;
    const selectedMerchant = stateMerchant || storedMerchantId;

    // reached from a garage record: only the accounts of that garage
    if (selectedBranch && selectedBranch !== ":selectedBranch") {
      this.setState(
        {
          selectedBranch,
          branchName: routeState.branchName || "",
          selectedMerchant: selectedMerchant || "",
          canWrite: this.hasPermission([Scopes.MERCHANTS_USERS_WRITE]),
        },
        () => {
          if (this.state.selectedMerchant) {
            this.callPage();
          } else {
            this.resolveMerchantFromBranch(selectedBranch);
          }
        }
      );
      return;
    }
    const canWrite = this.hasPermission([Scopes.MERCHANTS_USERS_WRITE])
    this.setState({ canWrite });
    if (selectedMerchant) {
      this.setState({ selectedMerchant }, this.callPage);
    } else {
      this.fetchMerchants();
    }
  }

  // Fetches the list of merchants when merchantId is not in sessionStorage
  fetchMerchants = async () => {
    const response = await this.engine.getItem("merchants");
    if (response && response.status === 200) {
      this.setState({
        isLoading: false,
        merchants: response.data.items.map((merchant) => ({
          value: merchant.id,
          label: merchant.name,
        })),
      });
      if (this.state.merchants.length === 1) {
        this.setState({
          selectedMerchant: this.state.merchants[0].value,
        });
        this.callPage();
      }
    }
  };

  // the users endpoint is per merchant, so find the merchant of this garage
  resolveMerchantFromBranch = async (branchId) => {
    const response = await this.engine.getItem(`merchants-branches/${branchId}`);
    const branch = response && response.data;
    if (branch && branch.merchant) {
      this.setState(
        {
          selectedMerchant: branch.merchant.id,
          branchName: this.state.branchName || branch.name || branch.address,
        },
        this.callPage
      );
    } else {
      this.setState({ isLoading: false });
    }
  };

  // Fetches the list of users for the selected merchant
  fetchUsers = async () => {
    const { selectedMerchant, selectedBranch } = this.state;
    const response = await this.engine.getItem(
      `merchants/${selectedMerchant}/users`
    );
    if (response && response.status === 200) {
      const items = selectedBranch
        ? response.data.items.filter(
            (item) => item.branch && item.branch.id === selectedBranch
          )
        : response.data.items;
      this.setState({
        isLoading: false,
        dataSource: items.map((item, key) => ({
          id: key,
          data: item,
          user: item.user.firstName,
          email: item.user.email,
          scope: (item?.scope || []).join(", ") ?? "",
          latitude: item.latitude,
          longitude: item.longitude,
          created: new Date(item.createdAt),
          actions: this.handleActions(key, item),
        })),
      });
    }
    this.props.loadingAction(false);
  };

  handlePage = () => {
    this.props.loadingAction(true);
    this.callPage();
  };

  callPage = () => {
    if (this.state.selectedMerchant) {
      this.fetchUsers();
    }
  };

  async handleDelete(valueId) {
    if (window.confirm(strings.confirmdelete)) {
      this.props.loadingAction(true);
      const { selectedMerchant } = this.state;
      const result = await this.engine.deleteItem(
        `merchants/${selectedMerchant}/users`,
        valueId
      );
      if (result) {
        this.handlePage();
      }
      this.props.loadingAction(false);
    }
  }

  handleEdit = (index, dataId) => {
    const data = this.state.dataSource[index].data;
    const { selectedMerchant } = this.state;
    this.props.history.push({
      pathname: `/admin/merchant/${selectedMerchant}/users/edit/${dataId}`,
      state: { selectedMerchant },
      data,
    });
  };

  handleActions(index, item) {
    return (
      <RowActions
        actions={[
          editAction(() => this.handleEdit(index, item.id), this.state.canWrite),
          deleteAction(() => this.handleDelete(item.id), this.state.canWrite),
        ]}
      />
    );
  }

  // Updates selected merchant and fetches data
  handleMerchantSelect = (event) => {
    const selectedMerchant = event.value;
    this.setState({ selectedMerchant }, () => {
      this.props.history.push(`/admin/merchant/${selectedMerchant}/users`);
      this.callPage();
    });
  };

  handleAdd = () => {
    const { selectedMerchant } = this.state;
    if (!selectedMerchant) {
      alert(strings.select + " " + strings.merchant);
      return;
    }
    this.props.history.push({
      pathname: `/admin/merchant/${selectedMerchant}/users/add`,
      state: { selectedMerchant },
    });
  };

  render() {
    const { dataSource, isLoading, merchants, selectedMerchant, canWrite } = this.state;
    let columns = [
      {
        Header: strings.user,
        accessor: "user",
      },
      {
        Header: strings.email,
        accessor: "email",
      },
      {
        Header: strings.scope,
        accessor: "scope",
      },
      {
        Header: strings.created,
        accessor: "created",
        Cell: (row) => row.value.toLocaleDateString(),
      },
      {
        Header: strings.actions,
        accessor: "actions",
        sortable: false,
        filterable: false,
      },
    ];

    return (
      <div className="content">
        <GarageSectionHeader
          branchId={this.state.selectedBranch}
          branchName={this.state.branchName}
          merchantId={this.state.selectedMerchant}
          active="users"
          history={this.props.history}
        />
        <Row>
          <Col md="12">
            <Card>
              <CardHeader>
                {canWrite && (
                  <Button
                    color="primary"
                    className="pull-right"
                    onClick={this.handleAdd}
                  >
                    {strings.add}
                  </Button>
                )}
                {!sessionStorage.getItem("merchantId") &&
                  merchants.length !== 1 && (
                    <SelectRow
                      label={strings.merchant}
                      name="selectedMerchant"
                      defaultValue={selectedMerchant}
                      data={merchants}
                      changeInput={this.handleMerchantSelect}
                      placeholder={strings.select}
                    />
                  )}
              </CardHeader>
              <CardBody>
                <ReactTable
                  previousText={strings.previous}
                  nextText={strings.next}
                  loadingText={strings.loading}
                  noDataText={strings.nodata}
                  pageText={strings.page}
                  data={dataSource}
                  defaultFilterMethod={this.filterCaseInsensitive}
                  filterable
                  loading={isLoading}
                  columns={columns}
                  defaultPageSize={10}
                  showPaginationTop={false}
                  showPaginationBottom
                  PaginationComponent={TablePagination}
                  className="-striped -highlight primary-pagination"
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(MerchantUserList);
